import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  api,
  useGetQuery,
  useGetQueryCollection,
  usePostMutation,
  usePutMutation,
  type CollectionSuccessApiResponse,
  useGetInfiniteList,
} from 'shared/lib/api';
import { logError } from 'shared/lib/log-errors';
import { pathToUrl } from 'shared/lib/router';
import { mapToOptions } from 'shared/lib/mappers';
import { DEFAULT_ERROR_INFINITY_FILTERS } from 'shared/lib/notifications';

import type {
  ContractorModel,
  CreateContractorDataModel,
  GetContractorModel,
  ImpersonateTokenModel,
  ContractorAvailablePromoModel,
  ContractorActivePromosModel,
} from '../model';
import { ManagerContractorsSort } from '../model';
import { useGetManagerContractorFilter } from '../lib';

import { contractorsRoutes } from './routes';

const contractorsKey = createQueryKeys('contractors', {
  contractorsList: [''],
});

export const useGetContractors = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<ContractorModel>(contractorsRoutes.internal.getContractors, {
    queryKey: [contractorsKey.contractorsList.queryKey, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });

export const useCreateContractor = () => {
  const urlFilters = useGetManagerContractorFilter();
  const getContractorsQuery = useGetContractors({ enabled: false, urlFilters });

  return usePostMutation<CreateContractorDataModel, { id: number }>(contractorsRoutes.internal.createContractor, {
    onSuccess: () => {
      getContractorsQuery.refetch().catch(logError);
    },
  });
};

export const useGetContractor = (id: number) =>
  useGetQuery<GetContractorModel>(contractorsRoutes.internal.getContractor, {
    urlParams: { id },
    cacheTime: 0,
  });

export const useUpdateContractor = (id: number) => {
  const urlFilters = useGetManagerContractorFilter();
  const getContractorsQuery = useGetContractors({ enabled: false, urlFilters });

  return usePutMutation<CreateContractorDataModel>(pathToUrl(contractorsRoutes.internal.updateContractor, { id }), {
    toastOptions: {
      successText: 'Your changes were saved successfully.',
    },
    onSuccess: () => {
      getContractorsQuery.refetch().catch(logError);
    },
  });
};

export const useGetContractorsForFilters = ({
  enabled = true,
  queryParams,
  staleTime,
  queryKey,
}: {
  enabled?: boolean;
  queryParams?: URLSearchParams | Record<string, string | number>;
  staleTime?: number;
  queryKey?: QueryKey;
} = {}) =>
  useGetQuery<Pick<ContractorModel, 'id' | 'companyName'>[]>(contractorsRoutes.internal.getContractorsList, {
    enabled,
    queryParams,
    staleTime,
    queryKey,
  });

export function getContractorsOptionsFromInfinite(
  data?: InfiniteData<CollectionSuccessApiResponse<Pick<ContractorModel, 'id' | 'companyName'>>>
) {
  return mapToOptions({
    items: data?.pages.flatMap(({ data }) => data.map((item) => item)),
    textField: ({ companyName }) => companyName,
  });
}

export function useGetContractorsInfinityList({ isSyncedWithUrl = false, filterName = '' } = {}) {
  return useGetInfiniteList<Pick<ContractorModel, 'id' | 'companyName'>>(
    contractorsRoutes.internal.getContractorsList,
    {
      defaultParams: { [ManagerContractorsSort.companyName]: 'asc' },
      isSyncedWithUrl,
      filterName,
      toastOptions: {
        errorOptions: {
          errorText: DEFAULT_ERROR_INFINITY_FILTERS,
          showErrorPriority: true,
        },
      },
    }
  );
}

export function useGetContractorsOptions() {
  return mapToOptions({ items: useGetContractorsForFilters().data?.data, textField: ({ companyName }) => companyName });
}

export const getImpersonateTokenFromBe = async (id: number | string): Promise<string> => {
  const res = await api.get<ImpersonateTokenModel>(pathToUrl(contractorsRoutes.internal.getImpersonateToken, { id }));
  const token = res.data.data.token;

  if (!token) {
    throw new Error("Impersonate token doesn't exist");
  }

  return token;
};

export const useGetContractorActivePromos = (id: number, additivePromos: string) =>
  useGetQuery<ContractorActivePromosModel>(contractorsRoutes.internal.getActivePromos, {
    urlParams: { id },
    queryParams: additivePromos ? { promos: additivePromos } : undefined,
  });

export const useGetContractorAvailablePromos = (id: number, additivePromos: string) =>
  useGetQuery<ContractorAvailablePromoModel[]>(contractorsRoutes.internal.getAvailablePromos, {
    urlParams: { id },
    queryParams: additivePromos ? { promos: additivePromos } : undefined,
  });

export const useUpdatePromos = (id: number) =>
  usePostMutation<{ promos: string }>(pathToUrl(contractorsRoutes.internal.updateContractorPromos, { id }));
